import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { inventoryErrorKey } from '@/features/inventory/inventoryErrors';
import { Caution, Dialog, ErrorNote, SECONDARY_BUTTON } from '@/features/inventory/registerUi';
import type { AdminDevice } from '@/features/inventory/useInventory';
import { useCommissionDevice } from './useCommissioning';

/**
 * COMMISSION A TRACKER — put it in Traccar before any customer exists.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THE DIALOG SAYS WHAT THE BUTTON ACTUALLY DOES, IN PLAIN WORDS.
 * ─────────────────────────────────────────────────────────────────────────────
 * "This creates the tracker in Traccar. It will start reporting immediately and
 * will belong to no customer." That is three consequences an operator cannot
 * infer from the word "Commission", and one of them (it starts reporting) is the
 * whole reason to do it — the unit can then be watched on `/commissioning`
 * before it is sold.
 *
 * There is NO name field by default and the server defaults the Traccar name to
 * the IMEI. 🔴 NEVER A PLATE: there is no vehicle yet, and a plate here would be
 * a fact about a customer who has not bought this unit.
 */
export function CommissionDialog({
  device,
  onClose,
}: {
  device: AdminDevice;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const commission = useCommissionDevice();
  const [errorKey, setErrorKey] = useState<string | null>(null);

  async function run() {
    setErrorKey(null);
    try {
      await commission.mutateAsync({ deviceId: device.id });
    } catch (e) {
      setErrorKey(inventoryErrorKey(e));
    }
  }

  const done = commission.data;

  return (
    <Dialog title={t('commissioning.dialog.title', { imei: device.imei })} onClose={onClose}>
      {done ? (
        <div className="grid gap-3">
          <p className="rounded-lg border border-raad-primary/40 bg-raad-primary/10 p-3 text-[12.5px]">
            {t('commissioning.dialog.done')}
          </p>
          {/* 🔴 ADOPTION IS TOLD, NEVER SILENT. An earlier attempt that crashed
              after Traccar created the device would otherwise look identical to
              a fresh create, and the operator would never learn that a
              half-finished attempt existed. */}
          {done.adopted ? <Caution>{t('commissioning.dialog.adopted')}</Caution> : null}
          {/* ────────────────────────────────────────────────────────────────
              🔴 RESTART THE TRACKER — the other end of the trap the
              decommission dialog warns about, and it is UNCONDITIONAL.
              ────────────────────────────────────────────────────────────────
              Traccar resolves a device once, when the TCP session opens, and
              holds it for the life of that session. A tracker that was already
              switched on and connected therefore keeps reporting against
              whatever it was bound to before — the registration just deleted, or
              nothing at all — and this brand-new registration receives silence.
              Measured on production 1 Sep: id 6 had 49 positions, its
              re-commissioned successor id 8 had zero.

              🔴 IT IS NOT CONDITIONAL ON "IS THIS A RE-COMMISSION", DELIBERATELY.
              `AdminDevice` carries no link history, so answering that question
              client-side needs a new API field — and the instruction would be
              WORTH PRINTING ANYWAY, because a first commission has the same
              failure whenever the installer powered the unit up before
              registering it, which is the ordinary order of work. So the
              sentence is worded to be true in both cases and shown in both,
              rather than made precise at the cost of being absent when it
              matters. It costs an operator four seconds and a power button.

              It is on the SUCCESS panel, not before the press: the restart has
              to happen after the registration exists, or it binds to nothing
              again. */}
          <Caution>
            <span className="block font-semibold">{t('commissioning.dialog.restartTitle')}</span>
            <span className="mt-1 block">{t('commissioning.dialog.restartBody')}</span>
          </Caution>
          <p className="text-[12.5px] text-raad-muted">
            {t('commissioning.dialog.watchIt')}{' '}
            <Link to="/commissioning" className="text-raad-accent underline">
              {t('commissioning.dialog.watchItLink')}
            </Link>
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          <p className="text-[13px]">{t('commissioning.dialog.explainer')}</p>
          <p className="text-[12.5px]">
            <span className="text-raad-muted">{t('inventory.devices.imei')}: </span>
            <span className="num">{device.imei}</span>
            {device.model ? <span className="text-raad-muted"> · {device.model}</span> : null}
          </p>
          {/* A fitted SIM with no number is ACCEPTED by the server (ADR 0014 §2:
              a bulk-procured SIM has no number until the carrier issues one) but
              it will not report, so the wait on `/commissioning` would be
              endless and unexplained. Warned, never blocked. */}
          {device.simId !== null && device.simMsisdn === null ? (
            <Caution>{t('provision.device.simNotActivatedWarning')}</Caution>
          ) : null}
        </div>
      )}

      <ErrorNote errorKey={errorKey} />

      <div className="mt-5 flex items-center justify-end gap-3">
        <button type="button" className={SECONDARY_BUTTON} onClick={onClose}>
          {t('inventory.close')}
        </button>
        {done ? null : (
          <button
            type="button"
            onClick={() => void run()}
            disabled={commission.isPending}
            className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary disabled:opacity-40"
          >
            {commission.isPending
              ? t('commissioning.dialog.working')
              : t('commissioning.dialog.confirm')}
          </button>
        )}
      </div>
    </Dialog>
  );
}
